import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Card, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaCalendarDay, FaStopwatch, FaBoxOpen, FaUserSlash, FaUser, FaSignInAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import supervisorApi from '../../services/supervisorApi';
import taskApi from '../../services/taskApi';
import EditTaskModal from '../EditTaskModal';
import useLanguage from '../../hooks/useLanguage';
import { formatRelativeTime, minutesBetween } from '../../utils/dateFormatter';
import { taskRefusalKey } from '../../utils/taskErrorCodes';
import { TASK_TYPE_BADGES } from '../../config/taskTypes';

/**
 * «المتابعة» — what needs the supervisor's attention now (tasks/supervisor-view.md).
 *
 * A briefing, not a second working list: four counts, one short ranked list, one clean-time line, and
 * deliberately NO filters of its own (D20) — drilling down means switching to the task list beside it.
 *
 * Everything shown is decided on the server. The verdicts arrive as codes and are worded here
 * (I18N_STRATEGY); no date arithmetic happens in the browser.
 */

/**
 * The four tiles. There is deliberately no "overdue" count: every row already carries how late it
 * is, in red, and the list is ordered by it — a tile would repeat the loudest thing on the page.
 * The clean-time figure takes that slot instead, where it can actually be seen (founder, 20 Sep).
 *
 * «مهام غير مسندة» replaced «شكاوى تنتظر» (tasks/followup-tasks-only.md F1/F2): this tab lists tasks
 * only. It counts repairs and refills with no owner — those are owned from creation, so the goal is 0.
 * Cleans are not counted: one with no owner is normal until near its checkout (founder, 14 Oct).
 */
const COUNT_TILES = [
  { key: 'dueToday', icon: <FaCalendarDay />, color: '#0d6efd' },
  { key: 'cleanTime', icon: <FaStopwatch />, color: '#198754' },
  { key: 'openRefills', icon: <FaBoxOpen />, color: '#fd7e14' },
  { key: 'needsOwner', icon: <FaUserSlash />, color: '#6f42c1', subKey: 'tasks.attention.needsOwnerGoal' }
];

/** «بلا مسؤول» on a row the ownership rule flags — the readable orange this tab already uses. */
const NEEDS_OWNER_COLOUR = '#a14a00';

/**
 * The colour a row's kind is marked in — the same navy and orange the task cards already use
 * (`TASK_TYPE_BADGES`), so one type means one colour across the page.
 *
 * Note the deliberate difference from the cards: there, cleaning carries NO badge, because a mark
 * everything has marks nothing. Here every row is marked, because this list mixes cleans, repairs,
 * supplies and units — the kind is information, not decoration.
 */
const KIND_COLOUR = {
  cleaning: '#0d6efd',
  maintenance: TASK_TYPE_BADGES.maintenance.color,
  restock: TASK_TYPE_BADGES.restock.color,
  // A unit needing a decision (unit-operational-status §9 item 3): red when it is stopped, grey when
  // its status cannot be worked out — the same two tones the unit page uses.
  unitStopped: '#dc3545',
  unitUnknown: '#6c757d'
};

/** The colour key for a row — its kind, or for a task its type. */
const colourKeyOf = (row) => {
  if (row.kind === 'unit_status') return row.type === 'STOPPED' ? 'unitStopped' : 'unitUnknown';
  return row.type;
};

/**
 * A clean under way: how long it has run and, when it is timed, against which target — the same
 * target the crew's timer counts to (tasks/cleaning-timer.md D9). Past the target it turns the
 * crew card's orange, in a shade dark enough to read as text; "over target", never "late".
 */
const RunningFor = ({ row, asOf, t, num }) => {
  const elapsed = minutesBetween(row.startedAt, asOf);
  // Judged on the exact time, not the rounded minutes shown: rounding held "over target" back for
  // up to a minute after the crew's own clock had already turned (found in the dev walkthrough).
  const over = Boolean(row.targetMinutes)
    && new Date(asOf).getTime() - new Date(row.startedAt).getTime() > row.targetMinutes * 60000;
  const unit = t('tasks.attention.cleanTime.unit');
  return (
    <span className={over ? undefined : 'text-success'} style={over ? { color: '#a14a00' } : undefined}>
      {t('tasks.attention.runningFor')} {num(elapsed)} {unit}
      {row.targetMinutes ? ` · ${t('tasks.attention.targetOf')} ${num(row.targetMinutes)} ${unit}` : null}
      {over && ` · ${t('tasks.attention.overTarget')}`}
    </span>
  );
};

const AttentionTab = ({ refreshSignal = 0, onOpenPerformance, openTaskId = null, onDeepLinkHandled }) => {
  const { t, language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // A translation KEY, not a sentence. Two reasons: the words belong to render time, not fetch time
  // (I18N_STRATEGY), and `t` is rebuilt on every render by `useLanguage` — holding it as a fetch
  // dependency re-created `load` each render and the page refetched itself forever.
  const [errorKey, setErrorKey] = useState(null);
  const [chasing, setChasing] = useState(null);
  // The task behind a row, opened in place. `EditTaskModal` needs the whole task, not its id — the
  // same shape `GuestReviewsPage` already uses to open a clean from a review row.
  const [editingTask, setEditingTask] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setErrorKey(null);
    try {
      const res = await supervisorApi.getWorklist();
      setData(res.data.data);
    } catch (err) {
      setErrorKey('tasks.attention.error');
    } finally {
      setLoading(false);
    }
  }, []);

  // `refreshSignal` is bumped by the page's refresh button, which otherwise reloads the working list
  // behind this tab and appears to do nothing.
  useEffect(() => { load(); }, [load, refreshSignal]);

  /** Record a chase and show the new count on the row. The task itself does not move. */
  const chase = async (row) => {
    setChasing(row.id);
    setErrorKey(null);
    try {
      const res = await supervisorApi.chaseTask(row.id);
      const chases = res.data.data.chases;
      setData(prev => ({
        ...prev,
        items: prev.items.map(i => (i.id === row.id ? { ...i, chases } : i))
      }));
    } catch (err) {
      setErrorKey(taskRefusalKey(err.response?.data) || 'tasks.attention.chaseFailed');
    } finally {
      setChasing(null);
    }
  };

  /**
   * Western digits, in Arabic too (founder, 20 Sep).
   *
   * A deliberate exception to `formatCount`, which renders Arabic-Indic (٠١٢٣) for `ar` per the
   * design system's locale standard. Two reasons it holds HERE: these are quantities a supervisor
   * scans and compares at a glance rather than reads, and the page's own header line directly above
   * the tabs already prints «إجمالي 38 مهمة • 5 حرجة» in Western digits — two numeral systems on one
   * screen read as a fault. Dates and times keep the shared formatters and stay Arabic-Indic.
   */
  const num = (n) => String(n);

  /** Open a task row in its own dialog. */
  const openRow = async (row) => {
    setOpeningId(row.id);
    setErrorKey(null);
    try {
      const resp = await taskApi.getTask(row.id);
      const task = resp?.data?.task;
      if (task && task._id) setEditingTask(task);
      else setErrorKey('tasks.attention.openFailed');
    } catch (err) {
      setErrorKey('tasks.attention.openFailed');
    } finally {
      setOpeningId(null);
    }
  };

  // A notification link (`?task=<id>`) opens that task straight away, in the same dialog a row opens.
  // Handled once per id: the page clears the parameter, so closing the dialog leaves the list.
  useEffect(() => {
    if (!openTaskId) return;
    openRow({ id: openTaskId, kind: 'task' });
    if (onDeepLinkHandled) onDeepLinkHandled();
  }, [openTaskId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** "32 minutes late" / "3 hours late" / "2 days late" — the largest unit that still reads true. */
  const lateText = (minutes) => {
    if (minutes < 60) return `${num(minutes)} ${t('tasks.attention.lateMinutes')}`;
    if (minutes < 1440) return `${num(Math.floor(minutes / 60))} ${t('tasks.attention.lateHours')}`;
    return `${num(Math.floor(minutes / 1440))} ${t('tasks.attention.lateDays')}`;
  };

  const chaseText = (chases) => {
    if (!chases || !chases.count) return null;
    return chases.count === 1
      ? t('tasks.attention.chasedOnce')
      : `${num(chases.count)} ${t('tasks.attention.chasedTimes')}`;
  };

  if (loading) {
    return (
      <div className="text-center py-5 text-muted">
        <Spinner animation="border" size="sm" />
        <span style={{ marginInlineStart: '0.5rem' }}>{t('tasks.attention.loading')}</span>
      </div>
    );
  }

  if (!data) {
    return (
      <Alert variant="danger" className="d-flex justify-content-between align-items-center">
        <span>{t(errorKey || 'tasks.attention.error')}</span>
        <Button size="sm" variant="outline-danger" onClick={load}>{t('tasks.attention.retry')}</Button>
      </Alert>
    );
  }

  const { counts, items, total, cleanTime } = data;
  const hidden = total - items.length;

  return (
    <div>
      <p className="text-muted small mb-3">{t('tasks.attention.intro')}</p>

      {errorKey && (
        <Alert variant="warning" onClose={() => setErrorKey(null)} dismissible>{t(errorKey)}</Alert>
      )}

      <Row className="mb-4 g-3">
        {COUNT_TILES.map(tile => {
          const isCleanTime = tile.key === 'cleanTime';
          const value = isCleanTime ? cleanTime.averageMinutes : counts[tile.key];
          // `null` is "nothing to average" for the clean time, and a count the server could not give.
          // Neither is the same answer as zero, so neither prints one.
          const withheld = value === null || value === undefined;
          // What it counts, said in the tile rather than in a paragraph underneath.
          const sub = isCleanTime
            ? `${t('tasks.attention.cleanTime.window')}${withheld ? '' : ` · ${num(cleanTime.sampleSize)} ${t('tasks.attention.cleanTime.sample')}`}`
            : (tile.subKey ? t(tile.subKey) : null);
          return (
            <Col key={tile.key} xs={6} md={3}>
              {/* The clean-time tile opens «تقارير الأداء» — the same figure, per cleaner. */}
              <Card
                className="h-100 border-0 touch-card hm-tile"
                {...(isCleanTime && onOpenPerformance ? {
                  role: 'button',
                  tabIndex: 0,
                  style: { cursor: 'pointer' },
                  title: t('tasks.performance.openFromTile'),
                  onClick: onOpenPerformance,
                  onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') onOpenPerformance(); }
                } : {})}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h2 className="mb-0" style={{ color: withheld ? '#adb5bd' : tile.color }}>
                        {withheld ? '—' : num(value)}
                        {isCleanTime && !withheld && (
                          <small className="text-muted fs-6"> {t('tasks.attention.cleanTime.unit')}</small>
                        )}
                      </h2>
                      <small className="text-muted">
                        {isCleanTime ? t('tasks.attention.cleanTime.title') : t(`tasks.attention.counts.${tile.key}`)}
                      </small>
                      {sub && <div><small className="text-muted fst-italic">{sub}</small></div>}
                    </div>
                    <div className="fs-4" style={{ color: tile.color, opacity: 0.3 }}>{tile.icon}</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Card className="mb-4">
        <Card.Body className="p-0">
          {items.length === 0 ? (
            <div className="text-center py-5">
              <p className="mb-1 fw-semibold">{t('tasks.attention.emptyTitle')}</p>
              <p className="text-muted small mb-0">{t('tasks.attention.emptyHint')}</p>
            </div>
          ) : (
            items.map((row, index) => (
              <div
                key={`${row.kind}-${row.id}`}
                className="p-3 d-flex flex-column flex-md-row justify-content-between gap-2"
                style={{
                  borderTop: index === 0 ? 'none' : '1px solid #f1f3f5',
                  // The one accent on the page: a guest is walking in, which is why this row is here.
                  borderInlineStart: row.guestArrivalAt ? '3px solid #0d6efd' : '3px solid transparent'
                }}
              >
                <div className="min-w-0">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Badge
                      bg={null}
                      style={{ backgroundColor: KIND_COLOUR[colourKeyOf(row)] || '#6c757d' }}
                    >
                      {row.kind === 'unit_status' && t('tasks.attention.unitStatus')}
                      {row.kind === 'task' && t(`tasks.type.${row.type}`)}
                    </Badge>
                    <span className="fw-semibold">{row.unit?.name || '—'}</span>
                    {row.unit?.code && <small className="text-muted">{row.unit.code}</small>}
                    {row.ticketId && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none"
                        disabled={openingId === row.id}
                        onClick={() => openRow(row)}
                      >
                        <small>{row.ticketId}</small>
                      </Button>
                    )}
                  </div>

                  {/* WHAT the job is. The badge says the kind and the unit says where, which is the
                      whole story for a clean or a refill — their titles are formulaic and a refill
                      lists its items below anyway. For a repair it is the only thing that
                      distinguishes one row from another: prod opened with two problems on one
                      booking, one about a smell and one about a fridge, and they read identically. */}
                  {row.title && row.type === 'maintenance' && (
                    <div className="text-truncate" style={{ maxWidth: '46rem' }}>{row.title}</div>
                  )}
                  {/* A unit row has no title — its reason IS what distinguishes it (codes worded here). */}
                  {row.kind === 'unit_status' && (
                    <div>{t(`tasks.attention.unitReason.${row.reasonCode}`)}</div>
                  )}

                  <div className="text-muted small mt-1 d-flex align-items-center gap-3 flex-wrap">
                    {/* A unit has no assignee — «بلا مسؤول» there would read as a gap in the work. */}
                    {/* No owner is the one gap the ownership rule names, so it is not left in grey. */}
                    {row.kind !== 'unit_status' && (
                      <span
                        className={`d-flex align-items-center gap-1${row.needsOwner ? ' fw-semibold' : ''}`}
                        style={row.needsOwner ? { color: NEEDS_OWNER_COLOUR } : undefined}
                      >
                        <FaUser size={11} />
                        {row.owner?.name || t('tasks.attention.unassigned')}
                      </span>
                    )}
                    {(row.kind !== 'unit_status' || row.dueAt) && (
                      <span>
                        {row.dueAt
                          ? `${t(row.kind === 'unit_status' ? 'tasks.attention.reviewDueAt' : 'tasks.attention.dueAt')} ${formatRelativeTime(row.dueAt, language)}`
                          : t('tasks.attention.noDeadline')}
                      </span>
                    )}
                    {row.guestArrivalAt && (
                      <span className="text-primary d-flex align-items-center gap-1">
                        <FaSignInAlt size={11} />
                        {t('tasks.attention.guestArriving')} {formatRelativeTime(row.guestArrivalAt, language)}
                      </span>
                    )}
                    {/* A clean already under way, and for how long — the number a supervisor can
                        still act on. The finished duration lives on the task itself; by the time a
                        job is done it has left this list. */}
                    {row.taskStatus === 'in-progress' && row.startedAt && (
                      <RunningFor row={row} asOf={data.asOf} t={t} num={num} />
                    )}
                  </div>

                  {/* A refill task is ONE accountable item; its requests live beneath it, each
                      naming the clean that flagged it or the person who added it (D9). */}
                  {row.items && row.items.length > 0 && (
                    <ul className="text-muted small mt-2 mb-0" style={{ paddingInlineStart: '1.1rem' }}>
                      {row.items.map(item => (
                        <li key={item.itemId}>
                          {item.labelAr}
                          <span className="text-muted"> — {item.flaggedFromTaskId
                            ? t('tasks.attention.fromCleaning')
                            : t('tasks.attention.addedByPerson')}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  {row.status === 'overdue' && (
                    <Badge bg="danger" className="fw-normal">{lateText(row.overdueByMinutes)}</Badge>
                  )}
                  {chaseText(row.chases) && (
                    <small className="text-muted">{chaseText(row.chases)}</small>
                  )}
                  {row.kind === 'unit_status' && row.unit?.id && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="touch-btn"
                      onClick={() => navigate(`/properties/${row.unit.id}`)}
                    >
                      {t('tasks.attention.openUnit')}
                    </Button>
                  )}
                  {row.kind === 'task' && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="touch-btn"
                      disabled={chasing === row.id}
                      onClick={() => chase(row)}
                    >
                      {chasing === row.id ? t('tasks.attention.chasing') : t('tasks.attention.chase')}
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </Card.Body>
      </Card>

      {hidden > 0 && (
        <p className="text-muted small">{num(hidden)} {t('tasks.attention.moreItems')}</p>
      )}

      <EditTaskModal
        show={!!editingTask}
        task={editingTask}
        onHide={() => setEditingTask(null)}
        onTaskUpdated={() => { setEditingTask(null); load(); }}
      />
    </div>
  );
};

export default AttentionTab;
